import { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LoadErrorState } from '@/components/ListStates';
import { SettingsCard, SettingsPage, SettingsSection } from '@/components/settings/SettingsPage';
import {
  departmentsService, Department, DepartmentKind,
} from '@/services/departments.service';
import {
  Briefcase, Edit2, Plus, Trash2, GripVertical, Star, Save, X,
} from 'lucide-react';

const KIND_OPTIONS: { value: DepartmentKind; label: string }[] = [
  { value: 'sales',   label: 'Sales / intake' },
  { value: 'tech',    label: 'Technical / delivery' },
  { value: 'ops',     label: 'Operations' },
  { value: 'support', label: 'Customer support' },
  { value: 'medical', label: 'Medical' },
  { value: 'legal',   label: 'Legal' },
  { value: 'custom',  label: 'Other' },
];

export default function DepartmentsSettings() {
  const [items, setItems] = useState<Department[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Department | null>(null);
  const [editing, setEditing] = useState<Department | null>(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoadError(false);
    try {
      const list = await departmentsService.list();
      setItems(list);
    } catch {
      setLoadError(true);
    }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async (input: { name: string; description?: string; kind: DepartmentKind }) => {
    setBusy(true);
    try {
      await departmentsService.create(input);
      toast.success('Department created');
      setCreating(false);
      await load();
    } catch (e: any) {
      if (!e?.response) toast.error('Could not create department');
    } finally { setBusy(false); }
  };

  /** true when saved — the edit form stays open on failure so nothing typed is lost. */
  const onUpdate = async (deptUid: string, patch: Partial<Department>): Promise<boolean> => {
    setBusy(true);
    try {
      await departmentsService.update(deptUid, patch);
      await load();
      return true;
    } catch (e: any) {
      if (!e?.response) toast.error('Update failed');
      return false;
    } finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await departmentsService.remove(confirmDelete.deptUid);
      toast.success('Department deleted');
      setConfirmDelete(null);
      await load();
    } catch (e: any) {
      if (!e?.response) toast.error('Delete failed');
    } finally { setBusy(false); }
  };

  const onDragStart = (idx: number) => setDragIdx(idx);
  const onDragOver = (e: React.DragEvent) => e.preventDefault();
  const onDrop = async (toIdx: number) => {
    if (dragIdx === null || dragIdx === toIdx || !items) { setDragIdx(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(toIdx, 0, moved);
    setItems(next);
    setDragIdx(null);
    try {
      await departmentsService.reorder(next.map((d) => d.deptUid));
    } catch (e: any) {
      if (!e?.response) toast.error('Reorder failed');
      await load();
    }
  };

  const defaultName = items?.find((d) => d.isDefault)?.name;

  return (
    <SettingsPage
      eyebrow="Account"
      title="Departments"
      description="Teams that handle leads inside your company. A WhatsApp number can feed one department, and a teammate can belong to several."
      actions={items && items.length > 0 && !creating && (
        <Button iconLeft={<Plus size={14} />} onClick={() => setCreating(true)} disabled={busy}>Add department</Button>
      )}
    >
      <SettingsSection
        title="Your departments"
        description="Drag a row to change the order. The star marks the default — where new leads land when no other routing applies."
        aside={defaultName && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-2.5 py-1 text-2xs font-medium text-primary">
            <Star size={11} /> Default: {defaultName}
          </span>
        )}
      >
        <SettingsCard
          padded={false}
          footer={items && items.length > 0 ? (
            <span className="mr-auto text-xs text-text-muted">{items.length} department{items.length === 1 ? '' : 's'} · drag to reorder</span>
          ) : undefined}
        >
          {items === null && loadError ? (
            <LoadErrorState bare what="your departments" onRetry={() => void load()} />
          ) : items === null ? (
            <div className="space-y-2 p-5">{[1, 2, 3].map((i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-soft"><Briefcase size={20} /></div>
              <div className="text-sm font-medium text-text-primary">No departments yet</div>
              <p className="max-w-sm text-xs text-text-muted">Add one such as Sales or Support, then bind WhatsApp numbers and teammates to it so new leads reach the right people.</p>
              {!creating && (
                <Button size="sm" variant="secondary" iconLeft={<Plus size={13} />} onClick={() => setCreating(true)} disabled={busy}>Add the first department</Button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((d, idx) => (
                <li
                  key={d.deptUid}
                  draggable={!busy}
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(idx)}
                  className={clsx(
                    'group flex items-center gap-3 px-5 py-3.5 transition-colors',
                    dragIdx === idx ? 'bg-primary-soft' : 'hover:bg-surface-alt/60',
                  )}
                >
                  <span className="cursor-grab text-text-muted active:cursor-grabbing" aria-hidden><GripVertical size={16} /></span>
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white" style={{ backgroundColor: d.color }}>
                    {d.name.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="truncate text-sm font-semibold text-text-primary">{d.name}</span>
                      {d.isDefault && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2 py-0.5 text-2xs font-semibold text-primary">
                          <Star size={10} /> Default
                        </span>
                      )}
                      <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-muted">{KIND_OPTIONS.find((k) => k.value === d.kind)?.label ?? d.kind}</span>
                    </div>
                    {d.description && <div className="mt-0.5 truncate text-xs text-text-muted">{d.description}</div>}
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5 md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:group-focus-within:opacity-100">
                    {!d.isDefault && (
                      <button
                        onClick={() => onUpdate(d.deptUid, { isDefault: true })}
                        disabled={busy}
                        title="Make default"
                        aria-label={`Make ${d.name} the default department`}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-primary-soft hover:text-primary"
                      >
                        <Star size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => setEditing(d)}
                      disabled={busy}
                      aria-label={`Edit ${d.name}`}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => setConfirmDelete(d)}
                      disabled={busy}
                      aria-label={`Delete ${d.name}`}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-danger-soft hover:text-danger"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SettingsCard>

        {creating && (
          <DepartmentFormCard
            title="New department"
            initial={{ name: '', description: '', kind: 'custom' }}
            onCancel={() => setCreating(false)}
            onSubmit={onCreate}
            busy={busy}
          />
        )}

        {editing && (
          <DepartmentFormCard
            key={editing.deptUid}
            title={`Edit “${editing.name}”`}
            initial={{ name: editing.name, description: editing.description ?? '', kind: editing.kind, color: editing.color }}
            onCancel={() => setEditing(null)}
            onSubmit={async (next) => {
              if (await onUpdate(editing.deptUid, next)) setEditing(null);
            }}
            busy={busy}
            showColor
          />
        )}
      </SettingsSection>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete department?"
        message={`This will remove "${confirmDelete?.name}". Leads currently assigned to it stay put (their assignment becomes orphaned until reassigned).`}
        danger
        onCancel={() => setConfirmDelete(null)}
        onConfirm={onDelete}
      />
    </SettingsPage>
  );
}

// ─── Create/edit form card ────────────────────────────────────────────────

interface FormCardProps {
  title: string;
  initial: { name: string; description?: string; kind: DepartmentKind; color?: string };
  onCancel: () => void;
  onSubmit: (input: { name: string; description?: string; kind: DepartmentKind; color?: string }) => void | Promise<void>;
  busy?: boolean;
  showColor?: boolean;
}

function DepartmentFormCard({ title, initial, onCancel, onSubmit, busy, showColor }: FormCardProps) {
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description ?? '');
  const [kind, setKind] = useState<DepartmentKind>(initial.kind);
  const [color, setColor] = useState(initial.color ?? '#a18a44');

  return (
    <SettingsCard
      footer={(
        <>
          <Button variant="ghost" size="sm" iconLeft={<X size={13} />} onClick={onCancel}>Cancel</Button>
          <Button
            size="sm"
            iconLeft={<Save size={13} />}
            disabled={busy || !name.trim()}
            onClick={() => onSubmit({ name: name.trim(), description: description.trim() || undefined, kind, ...(showColor ? { color } : {}) })}
          >
            Save
          </Button>
        </>
      )}
    >
      <div className="mb-4 text-sm font-semibold text-text-primary">{title}</div>
      <FormField label="Name" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sales, Mobile App Team, Reception" maxLength={80} />
      </FormField>
      <FormField label="Description (optional)">
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description of what this team does" maxLength={300} />
      </FormField>
      <div className="grid gap-x-4 sm:grid-cols-[minmax(0,1fr)_auto]">
        <FormField label="Kind" hint="Sets the icon and default routing. Sales is the usual lead-intake department.">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as DepartmentKind)}
            className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </FormField>
        {showColor && (
          <FormField label="Colour">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-20 cursor-pointer rounded-lg border border-border"
            />
          </FormField>
        )}
      </div>
    </SettingsCard>
  );
}
